import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import GlassCard from '../ui/GlassCard';
import GlassButton from '../ui/GlassButton';
import SectionHeader from '../ui/SectionHeader';
import { discoverAppTabs } from '../../constants/content';
import { track, appUrlWithId } from '../../lib/analytics';
import { useSectionView } from '../../hooks/useSectionView';

function ImagePlaceholder({ icon, darkMode, label }) {
  return (
    <div className="text-center p-8">
      <div className={`text-6xl mb-4 ${darkMode ? 'text-main' : 'text-purple-600'}`}>
        {icon}
      </div>
      <p className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
        {label}
      </p>
    </div>
  );
}

export default function DiscoverApp() {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const sectionRef = useSectionView('discover_app', 6);
  const [activeTab, setActiveTab] = useState('api');
  const [imageErrors, setImageErrors] = useState({});

  const activeContent = discoverAppTabs.find(tab => tab.id === activeTab);
  const hasImageError = imageErrors[activeTab];

  const handleImageError = (tabId) => {
    setImageErrors(prev => ({ ...prev, [tabId]: true }));
  };

  return (
    <section
      ref={sectionRef}
      id="app"
      className={`py-24 ${darkMode ? 'bg-main-black' : 'bg-gray-50'}`}
    >
      <div className="mainCont">
        <SectionHeader
          label={t('discoverApp.label')}
          title={t('discoverApp.title')}
          highlightColor="purple"
        />

        {/* Tabs */}
        <div className="overflow-x-auto mb-12 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex items-center justify-start sm:justify-center gap-1 sm:gap-2 p-1.5 sm:p-2 rounded-full bg-main bg-opacity-10 w-fit mx-auto min-w-max">
            {discoverAppTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`font-semibold text-sm sm:text-[15px] rounded-full transition px-3 sm:px-6 py-1.5 sm:py-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-main hover:bg-main2 text-white'
                    : darkMode
                      ? 'text-gray-400 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t(`discoverApp.tabs.${tab.id}.label`)}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <GlassCard padding="xl" className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Text content */}
            <div>
              <h3 className={`titleFont text-3xl md:text-4xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t(`discoverApp.tabs.${activeTab}.title`)}
              </h3>

              <p className={`text-base md:text-lg mb-4 ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {t(`discoverApp.tabs.${activeTab}.description`)}
              </p>

              <div className={`p-4 rounded-xl mb-6 ${
                darkMode
                  ? 'bg-main/10 border border-main/20'
                  : 'bg-purple-50 border border-purple-100'
              }`}>
                <p className={`text-base font-medium ${
                  darkMode ? 'text-main' : 'text-purple-700'
                }`}>
                  {t(`discoverApp.tabs.${activeTab}.highlight`)}
                </p>
              </div>

              <GlassButton
                variant="primary"
                size="lg"
                href={activeTab === 'api' ? activeContent.href : appUrlWithId(activeContent.href)}
                onClick={() => {
                  const isApp = activeTab !== 'api';
                  if (isApp) {
                    track('launch_app_clicked', {
                      cta_id: `discover_${activeTab}`,
                      cta_text: t(`discoverApp.tabs.${activeTab}.cta`),
                      section: 'discover_app',
                      destination_url: activeContent.href,
                      destination_type: 'app'
                    });
                  }
                  track('cta_clicked', {
                    cta_id: `discover_${activeTab}`,
                    cta_text: t(`discoverApp.tabs.${activeTab}.cta`),
                    section: 'discover_app',
                    destination_url: activeContent.href,
                    destination_type: isApp ? 'app' : 'docs'
                  });
                }}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                }
              >
                {t(`discoverApp.tabs.${activeTab}.cta`)}
              </GlassButton>
            </div>

            {/* Image or placeholder */}
            <div className="flex items-center justify-center">
              {hasImageError ? (
                <ImagePlaceholder
                  icon={activeContent.icon}
                  darkMode={darkMode}
                  label={t('discoverApp.screenshotSoon')}
                />
              ) : (
                <img
                  src={activeContent.image}
                  alt={t(`discoverApp.tabs.${activeTab}.title`)}
                  className="w-full h-auto object-contain"
                  onError={() => handleImageError(activeTab)}
                />
              )}
            </div>
          </div>
        </GlassCard>
      </div>
    </section>
  );
}
